import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';

const SHOP_CATEGORIES = [
  '',
  '스튜디오',
  '드레스',
  '메이크업',
  '예복',
  '예물',
  '한복',
  '베뉴',
  '혼수',
  '기타',
  '알수없음',
];
const CONFIDENCE_TABS = [
  {
    key: 'ALL',
    label: '전체 신뢰도',
    value: null,
  },
  {
    key: '90',
    label: '90% 이상',
    value: 0.9,
  },
  {
    key: '95',
    label: '95% 이상',
    value: 0.95,
  },
  {
    key: '97',
    label: '97% 이상',
    value: 0.97,
  },
];
export default function AdminShopPreviewPage() {
  const [keyword, setKeyword] = useState('');
  const [shopCategory, setShopCategory] = useState('');
  const [shops, setShops] = useState([]);
  const [shopLoading, setShopLoading] = useState(false);
  const [confidenceTabKey, setConfidenceTabKey] = useState('ALL');
  const [selectedShopId, setSelectedShopId] = useState(null);
  const [selectedTabKey, setSelectedTabKey] = useState('ALL');
  const [onlyUsable, setOnlyUsable] = useState(true);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [rows] = useState(60);

  const [selectedImage, setSelectedImage] = useState(null);

  const fetchShops = async () => {
    try {
      setShopLoading(true);

      const res = await axios.get('/api/admin/cafe-articles/shops', {
        params: {
          keyword,
          shopCategory,
          page: 1,
          rows: 1000,
        },
      });

      const items = res.data?.data?.items || [];
      setShops(items);

      if (!selectedShopId && items.length > 0) {
        setSelectedShopId(items[0].id);
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || '업체 목록 조회 실패');
    } finally {
      setShopLoading(false);
    }
  };

  const fetchDetail = async ({
    shopId = selectedShopId,
    tabKey = selectedTabKey,
    nextPage = page,
  } = {}) => {
    if (!shopId) return;
    const selectedConfidence = CONFIDENCE_TABS.find((tab) => tab.key === confidenceTabKey)?.value ?? null;
    try {
      setDetailLoading(true);

      const res = await axios.get(`/api/admin/cafe-articles/shops/${shopId}`, {
        params: {
          shopImageCategoryId: tabKey,
          confidenceMin: selectedConfidence,
          onlyUsable: onlyUsable ? 1 : 0,
          page: nextPage,
          rows: 1000,
        },
      });

      setDetail(res.data?.data || null);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || '업체 미리보기 조회 실패');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchShops();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedShopId) return;

    fetchDetail({
      shopId: selectedShopId,
      tabKey: selectedTabKey,
      nextPage: page,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShopId, selectedTabKey, confidenceTabKey, page, onlyUsable]);

  const shop = detail?.shop;
  const tabs = detail?.tabs || [];
  const images = detail?.images || [];
  const total = Number(detail?.total || 0);
  const maxPage = Math.max(1, Math.ceil(total / rows));

  return (
    <Page>
      <Sidebar>
        <SidebarTitle>업체 미리보기</SidebarTitle>

        <SearchBox>
          <Input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="업체명 검색"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setSelectedShopId(null);
                setSelectedTabKey('ALL');
                setPage(1);
                fetchShops();
              }
            }}
          />

          <Select
            value={shopCategory}
            onChange={(e) => {
              setShopCategory(e.target.value);
              setSelectedShopId(null);
              setSelectedTabKey('ALL');
              setPage(1);
            }}
          >
            {SHOP_CATEGORIES.map((category) => (
              <option key={category || 'ALL'} value={category}>
                {category || '전체 분류'}
              </option>
            ))}
          </Select>

          <Button
            type="button"
            onClick={() => {
              setSelectedShopId(null);
              setSelectedTabKey('ALL');
              setPage(1);
              fetchShops();
            }}
          >
            검색
          </Button>
        </SearchBox>

        <UsableToggle>
          <input
            type="checkbox"
            checked={onlyUsable}
            onChange={(e) => {
              setOnlyUsable(e.target.checked);
              setPage(1);
            }}
          />
          서비스 노출 가능 이미지만 보기
        </UsableToggle>

        <ShopList>
          {shopLoading && <EmptyText>업체 불러오는 중...</EmptyText>}

          {!shopLoading && shops.length === 0 && (
            <EmptyText>업체가 없습니다.</EmptyText>
          )}

          {shops.map((item) => (
            <ShopItem
              key={item.id}
              $selected={Number(selectedShopId) === Number(item.id)}
              onClick={() => {
                setSelectedShopId(item.id);
                setSelectedTabKey('ALL');
                setPage(1);
              }}
            >
              <ShopName>{item.shopName}</ShopName>
              <ShopMeta>
                {item.shopCategory || '분류없음'} · 이미지 {item.imageCount || 0} · 글{' '}
                {item.articleCount || 0}
              </ShopMeta>
            </ShopItem>
          ))}
        </ShopList>
      </Sidebar>

      <Main>
        {!shop && (
          <CenterEmpty>좌측에서 업체를 선택해줘.</CenterEmpty>
        )}

        {/* {detailLoading && <CenterEmpty>불러오는 중...</CenterEmpty>} */}

        {shop && (
          <>
            <Hero>
              <HeroLeft>
                <CategoryBadge>{shop.shopCategory}</CategoryBadge>
                <HeroTitle>{shop.shopName}</HeroTitle>
                <HeroDesc>{shop.description}</HeroDesc>

                <HeroStats>
                  <StatCard>
                    <StatValue>{shop.totalImageCount || 0}</StatValue>
                    <StatLabel>이미지</StatLabel>
                  </StatCard>

                  <StatCard>
                    <StatValue>{shop.totalArticleCount || 0}</StatValue>
                    <StatLabel>후기 글</StatLabel>
                  </StatCard>

                  <StatCard>
                    <StatValue>
                      {shop.avgQualityScore == null
                        ? '-'
                        : Number(shop.avgQualityScore).toFixed(1)}
                    </StatValue>
                    <StatLabel>평균 품질</StatLabel>
                  </StatCard>
                </HeroStats>
              </HeroLeft>
            </Hero>

            <TabWrap>
              {tabs.map((tab) => (
                <TabButton
                  key={tab.key}
                  type="button"
                  $active={String(selectedTabKey) === String(tab.key)}
                  onClick={() => {
                    setSelectedTabKey(String(tab.key));
                    setPage(1);
                  }}
                >
                  <span>{tab.categoryName}</span>
                  <strong>{tab.imageCount || 0}</strong>
                </TabButton>
              ))}
            </TabWrap>
            <ConfidenceWrap>
              {CONFIDENCE_TABS.map((tab) => (
                <ConfidenceButton
                  key={tab.key}
                  type="button"
                  $active={confidenceTabKey === tab.key}
                  onClick={() => {
                    setConfidenceTabKey(tab.key);
                    setPage(1);
                  }}
                >
                  {tab.label}
                </ConfidenceButton>
              ))}
            </ConfidenceWrap>
            <Toolbar>
              <ToolbarText>
                총 {total}개 · {page}/{maxPage} 페이지
              </ToolbarText>

              <Pager>
                <SmallButton
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((v) => Math.max(1, v - 1))}
                >
                  이전
                </SmallButton>
                <SmallButton
                  type="button"
                  disabled={page >= maxPage}
                  onClick={() => setPage((v) => Math.min(maxPage, v + 1))}
                >
                  다음
                </SmallButton>
              </Pager>
            </Toolbar>

            {images.length === 0 ? (
              <CenterEmpty>이 분류에 이미지가 없습니다.</CenterEmpty>
            ) : (
              <Grid>
                {images.map((image) => (
                  <ImageCard
                    key={`${image.cafeArticleImageShopId}-${image.cafeArticleImageId}`}
                    onClick={() => setSelectedImage(image)}
                  >
                    <Thumb
                      src={image.imageUrl}
                      alt={image.imageSummary || ''}
                      onError={(e) => {
                        if (
                          image.fallbackImageUrl &&
                          e.currentTarget.src !== image.fallbackImageUrl
                        ) {
                          e.currentTarget.src = image.fallbackImageUrl;
                        }
                      }}
                    />

                    <ImageOverlay>
                      <OverlayTop>
                        <ImageTag>{image.shopImageCategoryName || '미분류'}</ImageTag>
                        <ImageTag>
                          {image.confidence == null
                            ? '-'
                            : `${Math.round(Number(image.confidence) * 100)}%`}
                        </ImageTag>
                      </OverlayTop>

                      <OverlayBottom>
                        {image.imageSummary || '설명 없음'}
                      </OverlayBottom>
                    </ImageOverlay>
                  </ImageCard>
                ))}
              </Grid>
            )}
          </>
        )}
      </Main>

      {selectedImage && (
        <ModalBackdrop onClick={() => setSelectedImage(null)}>
          <ModalBody onClick={(e) => e.stopPropagation()}>
            <ModalImageWrap>
              <ModalImage
                src={selectedImage.largeImageUrl || selectedImage.imageUrl}
                alt={selectedImage.imageSummary || ''}
                onError={(e) => {
                  if (
                    selectedImage.fallbackImageUrl &&
                    e.currentTarget.src !== selectedImage.fallbackImageUrl
                  ) {
                    e.currentTarget.src = selectedImage.fallbackImageUrl;
                  }
                }}
              />
            </ModalImageWrap>

            <ModalInfo>
              <ModalTitle>{selectedImage.shopImageCategoryName || '미분류'}</ModalTitle>

              <InfoRow>
                <InfoLabel>이미지 설명</InfoLabel>
                <InfoValue>{selectedImage.imageSummary || '-'}</InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>원문 업체명</InfoLabel>
                <InfoValue>{selectedImage.rawShopName || '-'}</InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>공식 업체명</InfoLabel>
                <InfoValue>{selectedImage.resolvedShopName || '-'}</InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>매칭 방식</InfoLabel>
                <InfoValue>{selectedImage.matchType || '-'}</InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>신뢰도</InfoLabel>
                <InfoValue>
                  {selectedImage.confidence == null
                    ? '-'
                    : `${Math.round(Number(selectedImage.confidence) * 100)}%`}
                </InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>매칭 이유</InfoLabel>
                <InfoValue>{selectedImage.reason || '-'}</InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>게시글</InfoLabel>
                <InfoValue>
                  {selectedImage.article?.articleUrl ? (
                    <ArticleLink
                      href={selectedImage.article.articleUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {selectedImage.article.title || '게시글 열기'}
                    </ArticleLink>
                  ) : (
                    selectedImage.article?.title || '-'
                  )}
                </InfoValue>
              </InfoRow>

              <CloseButton type="button" onClick={() => setSelectedImage(null)}>
                닫기
              </CloseButton>
            </ModalInfo>
          </ModalBody>
        </ModalBackdrop>
      )}
    </Page>
  );
}

const Page = styled.div`
  display: flex;
  min-height: 100vh;
  background: #f6f7f9;
  color: #111827;
`;

const Sidebar = styled.aside`
  width: 320px;
  flex-shrink: 0;
  border-right: 1px solid #e5e7eb;
  background: #ffffff;
  padding: 20px;
  overflow-y: auto;
`;

const SidebarTitle = styled.h1`
  margin: 0 0 16px;
  font-size: 20px;
  font-weight: 800;
`;

const SearchBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Input = styled.input`
  width: 100%;
  height: 40px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 0 12px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #2563eb;
  }
`;

const Select = styled.select`
  width: 100%;
  height: 40px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 0 12px;
  font-size: 14px;
  background: #ffffff;
  outline: none;

  &:focus {
    border-color: #2563eb;
  }
`;

const Button = styled.button`
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: #111827;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
`;

const UsableToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 14px 0;
  font-size: 13px;
  color: #374151;
`;

const ShopList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ShopItem = styled.button`
  width: 100%;
  border: 1px solid ${({ $selected }) => ($selected ? '#2563eb' : '#e5e7eb')};
  background: ${({ $selected }) => ($selected ? '#eff6ff' : '#ffffff')};
  border-radius: 12px;
  padding: 12px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }
`;

const ShopName = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: #111827;
`;

const ShopMeta = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
`;

const Main = styled.main`
  flex: 1;
  padding: 28px;
  overflow-y: auto;
`;

const CenterEmpty = styled.div`
  min-height: 360px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 15px;
`;

const EmptyText = styled.div`
  padding: 20px 0;
  color: #6b7280;
  font-size: 13px;
`;

const Hero = styled.section`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  padding: 28px;
`;

const HeroLeft = styled.div`
  max-width: 900px;
`;

const CategoryBadge = styled.div`
  display: inline-flex;
  height: 28px;
  align-items: center;
  padding: 0 10px;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 13px;
  font-weight: 700;
`;

const HeroTitle = styled.h2`
  margin: 12px 0 8px;
  font-size: 32px;
  font-weight: 900;
  letter-spacing: -0.04em;
`;

const HeroDesc = styled.p`
  margin: 0;
  color: #4b5563;
  font-size: 15px;
  line-height: 1.6;
`;

const HeroStats = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 22px;
`;

const StatCard = styled.div`
  min-width: 120px;
  border-radius: 14px;
  background: #f9fafb;
  padding: 14px;
`;

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 900;
`;

const StatLabel = styled.div`
  margin-top: 2px;
  color: #6b7280;
  font-size: 12px;
`;

const TabWrap = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 18px;
  overflow-x: auto;
  padding-bottom: 4px;
`;

const TabButton = styled.button`
  flex-shrink: 0;
  height: 40px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid ${({ $active }) => ($active ? '#111827' : '#e5e7eb')};
  border-radius: 999px;
  padding: 0 14px;
  background: ${({ $active }) => ($active ? '#111827' : '#ffffff')};
  color: ${({ $active }) => ($active ? '#ffffff' : '#374151')};
  font-size: 14px;
  cursor: pointer;

  strong {
    font-size: 13px;
    opacity: 0.8;
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 16px 0 12px;
`;

const ToolbarText = styled.div`
  color: #6b7280;
  font-size: 13px;
`;

const Pager = styled.div`
  display: flex;
  gap: 8px;
`;

const SmallButton = styled.button`
  height: 34px;
  border: 1px solid #d1d5db;
  border-radius: 9px;
  background: #ffffff;
  padding: 0 12px;
  color: #374151;
  font-size: 13px;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 760px) {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
`;

const ImageCard = styled.button`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  border: 0;
  padding: 0;
  overflow: hidden;
  background: #e5e7eb;
  cursor: pointer;
`;

const Thumb = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 10px;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.35),
    rgba(0, 0, 0, 0.05) 45%,
    rgba(0, 0, 0, 0.5)
  );
  // opacity: 0;
  transition: opacity 0.15s ease;

  ${ImageCard}:hover & {
    opacity: 1;
  }
`;

const OverlayTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 6px;
`;

const OverlayBottom = styled.div`
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ImageTag = styled.span`
  min-width: 0;
  border-radius: 999px;
  padding: 5px 8px;
  background: rgba(255, 255, 255, 0.9);
  color: #111827;
  font-size: 12px;
  font-weight: 800;
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(17, 24, 39, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
`;

const ModalBody = styled.div`
  width: min(1120px, 100%);
  max-height: calc(100vh - 64px);
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) 360px;
  background: #ffffff;
  border-radius: 18px;
  overflow: hidden;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }
`;

const ModalImageWrap = styled.div`
  background: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 520px;
`;

const ModalImage = styled.img`
  max-width: 100%;
  max-height: calc(100vh - 80px);
  object-fit: contain;
`;

const ModalInfo = styled.div`
  padding: 22px;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  margin: 0 0 18px;
  font-size: 20px;
  font-weight: 900;
`;

const InfoRow = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
`;

const InfoLabel = styled.div`
  margin-bottom: 4px;
  color: #6b7280;
  font-size: 12px;
  font-weight: 700;
`;

const InfoValue = styled.div`
  color: #111827;
  font-size: 14px;
  line-height: 1.5;
`;

const ArticleLink = styled.a`
  color: #2563eb;
  text-decoration: none;
  font-weight: 700;

  &:hover {
    text-decoration: underline;
  }
`;

const CloseButton = styled.button`
  width: 100%;
  height: 42px;
  margin-top: 18px;
  border: 0;
  border-radius: 10px;
  background: #111827;
  color: #ffffff;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
`;

const ConfidenceWrap = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
  overflow-x: auto;
  padding-bottom: 4px;
`;

const ConfidenceButton = styled.button`
  flex-shrink: 0;
  height: 34px;
  border: 1px solid ${({ $active }) => ($active ? '#2563eb' : '#e5e7eb')};
  border-radius: 999px;
  padding: 0 12px;
  background: ${({ $active }) => ($active ? '#eff6ff' : '#ffffff')};
  color: ${({ $active }) => ($active ? '#1d4ed8' : '#4b5563')};
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    background: ${({ $active }) => ($active ? '#eff6ff' : '#f9fafb')};
  }
`;